import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Card,
    Typography,
    Modal,
    message,
    Tag,
    Tooltip,
    Popconfirm,
    Dropdown,
    Avatar,
    Row,
    Col,
    Alert,
    Input,
    Select,
    Checkbox,
    Spin,
    Empty
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    PlusOutlined,
    MoreOutlined,
    PictureOutlined,
    UserAddOutlined,
    LinkOutlined,
    ShareAltOutlined,
    QrcodeOutlined,
    DownloadOutlined,
    ClockCircleOutlined,
    CalendarOutlined,
    UserOutlined,
    TeamOutlined,
    StarOutlined,
    FireOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    LockOutlined,
    UnlockOutlined,
    OrderedListOutlined,
    AppstoreOutlined,
    TrophyOutlined,
    UploadOutlined,
    SearchOutlined,
    MessageOutlined,
    CopyOutlined,
    FileTextOutlined,
    CreditCardOutlined,
    CheckOutlined,
    FileExcelOutlined,
    SmileOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { updateAuctionStatus } from '../../redux/userSlice';
import auctionService from '../../services/auctionService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import ImageUpload from './ImageUpload';
import InviteParticipants from './InviteParticipants';
import BulkUploadUsers from './BulkUploadUsers';
import PageHeader from '../Common/PageHeader';
import useColumnVisibility from '../Common/ColumnVisibilityControl';
import env from '../../env';
import DirectLinkManager from './DirectLinkManager';
import ShareAuctionModal from './ShareAuctionModal';
import AuctionQRCodeModal from './AuctionQRCodeModal';
import QuickActionsToolbar from '../Common/QuickActionsToolbar';
import { auctionStorage } from '../../utils/storage';
import NoAuctionsFound from './NoAuctionsFound';
import { getAuctionStartTimeStatus } from '../../utils/dateUtils';
import { getTextSearchColumnProps, getNumberRangeColumnProps, getDateRangeColumnProps } from '../../utils/tableFilterHelpers';
import useIsMobile from '../../hooks/useIsMobile';

const { Title, Text } = Typography;

const UserAuctions = () => {
    const [auctions, setAuctions] = useState([]);
    // Currently visible rows after the table's own column filters/sorting are applied (used for CSV export)
    const [filteredAuctions, setFilteredAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [imageUploadVisible, setImageUploadVisible] = useState(false);
    const [inviteModalVisible, setInviteModalVisible] = useState(false);
    const [bulkUsersModalVisible, setBulkUsersModalVisible] = useState(false);
    const [selectedAuction, setSelectedAuction] = useState(null);
    const [directLinkModalVisible, setDirectLinkModalVisible] = useState(false);
    const [shareModalVisible, setShareModalVisible] = useState(false);
    const [qrModalVisible, setQrModalVisible] = useState(false);
    const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
    const [duplicatedAuction, setDuplicatedAuction] = useState(null);
    const [duplicateTitleModalVisible, setDuplicateTitleModalVisible] = useState(false);
    const [auctionToDuplicate, setAuctionToDuplicate] = useState(null);
    const [duplicateTitle, setDuplicateTitle] = useState('');
    const [duplicateSubmitting, setDuplicateSubmitting] = useState(false);
    const [duplicateSourceLots, setDuplicateSourceLots] = useState([]);
    const [loadingDuplicateSourceLots, setLoadingDuplicateSourceLots] = useState(false);
    const [selectedLotIdsToDuplicate, setSelectedLotIdsToDuplicate] = useState([]);
    const [lotTitleOverrides, setLotTitleOverrides] = useState({});
    const [duplicatedLotsCount, setDuplicatedLotsCount] = useState(0);
    const MAX_DUPLICATE_LOTS = 3;
    const [createAuctionInstructionsVisible, setCreateAuctionInstructionsVisible] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const isMobile = useIsMobile();

    useEffect(() => {
        // Only load auctions if user has auctions
        if (currentUser?.has_auctions) {
            loadAuctions();
        } else {
            setLoading(false);
        }
    }, [currentUser?.has_auctions]);

    const loadAuctions = async () => {
        try {
            setLoading(true);

            // Load all of the user's auctions in one request so the table's column
            // filters/sorting/pagination can work entirely client-side and dynamically
            // reflect whatever data is actually loaded.
            const response = await auctionService.getMyAuctions({ per_page: 1000 });

            if (response.data) {
                setAuctions(response.data);
                setFilteredAuctions(response.data);
                // Cache the auctions in local storage
                auctionStorage.saveAuctions(response.data);
            }
        } catch (error) {
            console.error('Error loading auctions:', error);
            // Try to load from cache if API fails
            const cachedAuctions = auctionStorage.getAuctions();
            if (cachedAuctions.length > 0) {
                setAuctions(cachedAuctions);
                setFilteredAuctions(cachedAuctions);
                message.warning('Showing cached auctions data');
            } else {
                message.error('Failed to load auctions');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (record) => {
        try {
            const response = await auctionService.deleteAuction(record.slug);
            if (response.status === 'success') {
                message.success('Auction deleted successfully');
                // Remove from cache
                auctionStorage.removeAuction(record.id);
                // Check if this was the last auction
                const remainingAuctions = auctions.filter(a => a.id !== record.id);
                if (remainingAuctions.length === 0) {
                    // Update user's auction status in Redux store
                    dispatch(updateAuctionStatus(false));
                }
                loadAuctions();
            } else {
                message.error(response.message || 'Failed to delete auction');
            }
        } catch (error) {
            console.error('Error deleting auction:', error);
            message.error(error.response?.data?.message || 'Failed to delete auction');
        }
    };

    const openDuplicateModal = async (record) => {
        const baseTitle = (record.title || '').replace(/\s*\(Copy\)\s*$/i, '').trim();
        setAuctionToDuplicate(record);
        setDuplicateTitle(baseTitle);
        setSelectedLotIdsToDuplicate([]);
        setLotTitleOverrides({});
        setDuplicateSourceLots([]);
        setDuplicateTitleModalVisible(true);

        try {
            setLoadingDuplicateSourceLots(true);
            const { data } = await auctionService.getLots(record.slug, { per_page: 100 });
            setDuplicateSourceLots(data || []);
        } catch (error) {
            console.error('Error loading lots for duplication:', error);
            setDuplicateSourceLots([]);
        } finally {
            setLoadingDuplicateSourceLots(false);
        }
    };

    const toggleLotSelectionForDuplicate = (lotId) => {
        setSelectedLotIdsToDuplicate((prev) => {
            if (prev.includes(lotId)) {
                return prev.filter((id) => id !== lotId);
            }
            if (prev.length >= MAX_DUPLICATE_LOTS) {
                message.warning(`You can only copy up to ${MAX_DUPLICATE_LOTS} lots`);
                return prev;
            }
            return [...prev, lotId];
        });
    };

    const handleLotTitleOverrideChange = (lotId, value) => {
        setLotTitleOverrides((prev) => ({ ...prev, [lotId]: value }));
    };

    const handleDuplicate = async () => {
        const title = duplicateTitle.trim();
        if (!title) {
            message.error('Auction title is required');
            return;
        }

        if (!auctionToDuplicate?.slug) {
            message.error('Unable to duplicate this auction');
            return;
        }

        try {
            setDuplicateSubmitting(true);
            const response = await auctionService.duplicateAuction(auctionToDuplicate.slug, { title });
            if (response.status !== 'success') {
                message.error(response.message || 'Failed to duplicate auction');
                return;
            }

            const newAuction = response.data;
            const lotIdsToCopy = selectedLotIdsToDuplicate.slice(0, MAX_DUPLICATE_LOTS);
            let copiedCount = 0;
            let failedCount = 0;

            for (const lotId of lotIdsToCopy) {
                const sourceLot = duplicateSourceLots.find((lot) => lot.id === lotId);
                if (!sourceLot) continue;

                try {
                    await auctionService.createLot(newAuction.slug, {
                        title: (lotTitleOverrides[lotId] || sourceLot.title || '').trim() || sourceLot.title,
                        description: sourceLot.description,
                        starting_price: sourceLot.starting_price,
                        quantity: sourceLot.quantity,
                        default_increment: sourceLot.default_increment,
                        is_reserve: sourceLot.is_reserve,
                        reserve_price: sourceLot.is_reserve ? sourceLot.reserve_price : null,
                        can_buy_now: sourceLot.can_buy_now,
                        buy_now_price: sourceLot.can_buy_now ? sourceLot.buy_now_price : null,
                        specifications: sourceLot.specifications,
                        video_url: sourceLot.video_url || null
                    });
                    copiedCount += 1;
                } catch (lotError) {
                    console.error('Error copying lot:', lotError);
                    failedCount += 1;
                }
            }

            if (failedCount > 0) {
                message.warning(`Auction duplicated, but ${failedCount} lot${failedCount > 1 ? 's' : ''} could not be copied`);
            } else {
                message.success('Auction duplicated successfully');
            }

            setDuplicateTitleModalVisible(false);
            setAuctionToDuplicate(null);
            setDuplicateTitle('');
            setSelectedLotIdsToDuplicate([]);
            setLotTitleOverrides({});
            setDuplicateSourceLots([]);
            setDuplicatedLotsCount(copiedCount);
            setDuplicatedAuction(newAuction);
            setDuplicateModalVisible(true);
            dispatch(updateAuctionStatus(true));
            loadAuctions();
        } catch (error) {
            console.error('Error duplicating auction:', error);
            message.error(error.response?.data?.message || 'Failed to duplicate auction');
        } finally {
            setDuplicateSubmitting(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            draft: 'default',
            scheduled: 'error',
            active: 'processing',
            completed: 'success',
            cancelled: 'purple'
        };
        return colors[status] || 'default';
    };

    // Table's own column filters/sorter run entirely client-side; capture the
    // resulting rows (before pagination) so CSV export reflects what's on screen.
    const handleTableChange = (_pagination, _filters, _sorter, extra) => {
        setFilteredAuctions(extra?.currentDataSource || auctions);
    };

    const handleImageUpload = (auction) => {
        setSelectedAuction(auction);
        setImageUploadVisible(true);
    };

    const handleImageUploadSuccess = () => {
        loadAuctions(); // Refresh the auctions list
    };

    // Export auctions to CSV
    const handleExportToCSV = () => {
        try {
            // Get filtered auctions or all auctions
            const dataToExport = filteredAuctions.length > 0 ? filteredAuctions : auctions;
            
            if (dataToExport.length === 0) {
                message.warning('No auctions to export');
                return;
            }

            // Prepare CSV headers
            const headers = [
                'Title',
                'Category',
                'Status',
                'Start Time',
                'Location',
                'Type',
                'Bidding Type',
                'Lots Count',
                'Featured',
                'Auction URL'
            ];

            // Prepare CSV rows
            const rows = dataToExport.map(auction => [
                auction.title || '',
                auction.category?.name || '',
                auction.status || '',
                auction.start_time ? moment(auction.start_time).format('YYYY-MM-DD HH:mm:ss') : '',
                auction.location || '',
                auction.is_private ? 'Private' : 'Public',
                auction.is_sequential ? 'Sequential' : 'Simultaneous',
                auction.lots_count || 0,
                auction.is_featured ? 'Yes' : 'No',
                `${env.FRONTEND_URL}/auctions/${auction.slug}`
            ]);

            // Combine headers and rows
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.map(cell => {
                    // Escape commas and quotes in cell values
                    const cellStr = String(cell || '');
                    if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                }).join(','))
            ].join('\n');

            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `auctions-export-${moment().format('YYYY-MM-DD-HHmmss')}.csv`);
            link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            URL.revokeObjectURL(url);

            message.success(`Exported ${dataToExport.length} auction(s) to CSV`);
        } catch (error) {
            console.error('Error exporting to CSV:', error);
            message.error('Failed to export auctions to CSV');
        }
    };

    // Dynamic filter option lists, built from whatever data is actually loaded
    const categoryFilters = useMemo(() => {
        const seen = new Map();
        auctions.forEach((auction) => {
            if (auction.category) {
                seen.set(auction.category.id, auction.category.name);
            }
        });
        return Array.from(seen, ([value, text]) => ({ text, value }));
    }, [auctions]);

    const statusFilters = useMemo(() => {
        const seen = new Set(auctions.map((auction) => auction.status).filter(Boolean));
        return Array.from(seen).map((status) => ({
            text: status.charAt(0).toUpperCase() + status.slice(1),
            value: status
        }));
    }, [auctions]);

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 300,
            fixed: isMobile ? undefined : 'left',
            ...getTextSearchColumnProps('title', 'Search title...'),
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
            render: (text, record) => (
                <Space>
                    <Avatar
                        shape="square"
                        size={50}
                        src={env.BACKEND_STORAGE_URL + '/' + record.image}
                        icon={!record.image && <PictureOutlined />}
                        style={{ 
                            width: 50, 
                            height: 50, 
                            borderRadius: 4, 
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f0f0f0'
                        }}
                    />
                    <Tooltip title={`${text} — click to manage lots`} placement="top">
                        <a
                            onClick={(e) => {
                                e.preventDefault();
                                localStorage.setItem('selectedAuctionId', record.id.toString());
                                navigate(`/my-lots?auction_id=${record.id}`);
                            }}
                            style={{
                                color: '#1890ff',
                                textDecoration: 'none',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.textDecoration = 'none';
                            }}
                        >
                            {text.length > 20 ? `${text.substring(0, 20)}...` : text}
                        </a>
                    </Tooltip>
                </Space>
            )
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            width: 140,
            filters: categoryFilters,
            onFilter: (value, record) => record.category?.id === value,
            render: (category) => (
                <Tag>
                        {category?.name || 'N/A'}
                </Tag>
            )
        },
        {
            title: 'Start Time',
            dataIndex: 'start_time',
            key: 'start_time',
            width: 180,
            ...getDateRangeColumnProps('start_time'),
            sorter: (a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf(),
            render: (date) => {
                const { statusText, statusColor, formattedDate, tooltipText } = getAuctionStartTimeStatus(date);
                
                return (
                    <Tooltip title={tooltipText}>
                        <div>
                            <div style={{ marginBottom: 2 }}>
                                {formattedDate}
                            </div>
                            {statusText && (
                                <Tag color={statusColor} size="small" style={{ marginTop: 4 }}>
                                    {statusText}
                                </Tag>
                            )}
                        </div>
                </Tooltip>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            filters: statusFilters,
            onFilter: (value, record) => record.status === value,
            render: (status, record) => {
                const getStatusIcon = (status) => {
                    const iconStyle = { marginRight: 4 };
                    switch (status) {
                        case 'draft': return <ExclamationCircleOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />;
                        case 'scheduled': return <ClockCircleOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
                        case 'active': return <FireOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
                        case 'completed': return <TrophyOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
                        case 'cancelled': return <ExclamationCircleOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
                        default: return <ExclamationCircleOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />;
                    }
                };

                const getStatusTooltip = (status) => {
                    switch (status) {
                        case 'draft': return 'Draft - Auction is in preparation mode';
                        case 'scheduled': return 'Scheduled - Auction is set to start at the specified time. Payment required to activate.';
                        case 'active': return 'Active - Auction is currently running and will start accepting bids when the start time is reached';
                        case 'completed': return 'Completed - Auction has ended and winners have been determined';
                        case 'cancelled': return 'Cancelled - Auction has been cancelled and is no longer available';
                        default: return 'Unknown status';
                    }
                };

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Tooltip title={getStatusTooltip(status)}>
                            <Tag color={getStatusColor(status)}>
                                {getStatusIcon(status)}
                                {status.toUpperCase()}
                            </Tag>
                        </Tooltip>
                        {status === 'scheduled' && (
                            <Tooltip title="Contact admin via WhatsApp">
                                <MessageOutlined 
                                    style={{ 
                                        fontSize: '18px', 
                                        color: '#25D366',
                                        cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const phoneNumber = '00905348293526';
                                        const message = encodeURIComponent('Hello, I need assistance with my scheduled auction.');
                                        window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
                                    }}
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Lots',
            dataIndex: 'lots_count',
            key: 'lots_count',
            width: 80,
            ...getNumberRangeColumnProps('lots_count'),
            sorter: (a, b) => (a.lots_count || 0) - (b.lots_count || 0),
            render: (count) => (
                <Tooltip title={`${count || 0} lot${(count || 0) !== 1 ? 's' : ''} in this auction`}>
                    <Space>
                        <AppstoreOutlined />
                        {count || 0}
                    </Space>
                </Tooltip>
            )
        },
        {
            title: 'Auction Type',
            dataIndex: 'is_private',
            key: 'is_private',
            width: 120,
            filters: [
                { text: 'Public', value: false },
                { text: 'Private', value: true }
            ],
            onFilter: (value, record) => Boolean(record.is_private) === value,
            render: (isPrivate) => (
                <Tooltip title={isPrivate ? 
                    'Private Auction - Only invited participants can view and bid' : 
                    'Public Auction - Anyone can view and participate'
                }>
                    <Space>
                        {/* {isPrivate ? <LockOutlined /> : <UnlockOutlined />} */}
                        <Tag color={isPrivate ? 'orange' : 'green'}>
                            {isPrivate ? 'PRIVATE' : 'PUBLIC'}
                        </Tag>
                    </Space>
                </Tooltip>
            )
        },
        {
            title: 'Bidding Type',
            dataIndex: 'is_sequential',
            key: 'is_sequential',
            width: 140,
            filters: [
                { text: 'Simultaneous', value: false },
                { text: 'Sequential', value: true }
            ],
            onFilter: (value, record) => Boolean(record.is_sequential) === value,
            render: (isSequential) => (
                <Tooltip title={isSequential ? 
                    'Sequential Bidding - Lots are auctioned one at a time in order' : 
                    'Simultaneous Bidding - All lots are available for bidding at the same time'
                }>
                    <Space>
                        {/* {isSequential ? <OrderedListOutlined /> : <AppstoreOutlined />} */}
                        <Tag color={isSequential ? 'blue' : 'purple'}>
                            {isSequential ? 'SEQUENTIAL' : 'SIMULTANEOUS'}
                        </Tag>
                    </Space>
                </Tooltip>
            )
        },
        {
            title: 'Featured',
            dataIndex: 'is_featured',
            key: 'is_featured',
            width: 120,
            filters: [
                { text: 'Featured', value: true },
                { text: 'Regular', value: false }
            ],
            onFilter: (value, record) => Boolean(record.is_featured) === value,
            render: (isFeatured) => (
                <Tooltip title={isFeatured ? 
                    'Featured Auction - Highlighted on the platform for better visibility' : 
                    'Regular Auction - Standard listing on the platform'
                }>
                    <Space>
                        {isFeatured ? <StarOutlined style={{ color: '#faad14' }} /> : <StarOutlined style={{ color: '#d9d9d9' }} />}
                        <Tag color={isFeatured ? 'gold' : 'default'}>
                            {isFeatured ? 'FEATURED' : 'REGULAR'}
                        </Tag>
                    </Space>
                </Tooltip>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            fixed: isMobile ? undefined : 'right',
            render: (_, record) => {
                const items = [
                    // Section 1: View & Edit
                    {
                        key: 'view',
                        label: 'View Auction',
                        icon: <EyeOutlined />,
                        onClick: () => window.open(`/auctions/${record.slug}`, '_blank', 'noopener,noreferrer')
                    },
                    {
                        key: 'edit',
                        label: 'Edit Auction',
                        icon: <EditOutlined />,
                        onClick: () => navigate(`/auctions/${record.slug}/edit`),
                        disabled: record.status === 'completed' || record.status === 'cancelled'
                    },
                    {
                        key: 'images',
                        label: 'Manage Images',
                        icon: <PictureOutlined />,
                        onClick: () => navigate(`/auctions/${record.slug}/images`),
                        disabled: record.status === 'completed' || record.status === 'cancelled'
                    },
                    {
                        type: 'divider'
                    },
                    // Section 2: Content Management
                  
                   
                    {
                        key: 'create_lot',
                        label: 'Create Lot',
                        icon: <PlusOutlined />,
                        onClick: () => navigate(`/lots/create?auction_slug=${record.slug}`),
                        disabled: record.status === 'completed' || record.status === 'cancelled'
                    },
                    {
                        key: 'bulk_upload',
                        label: 'Bulk Upload via CSV',
                        icon: <UploadOutlined />,
                        onClick: () => navigate(`/lots/bulk-upload?auction_slug=${record.slug}`),
                        disabled: record.status === 'completed' || record.status === 'cancelled'
                    },
                    {
                        key: 'manage_lots',
                        label: 'Manage Lots',
                        icon: <AppstoreOutlined />,
                        onClick: () => {
                            // Save auction ID to localStorage so MyLots can select it by default
                            localStorage.setItem('selectedAuctionId', record.id.toString());
                            navigate(`/my-lots?auction_id=${record.id}`);
                        }
                    },
                    {
                        type: 'divider'
                    },
                    // Section 3: Sharing & Promotion
                    {
                        key: 'share',
                        label: 'Share Auction',
                        icon: <ShareAltOutlined />,
                        onClick: () => {
                            setSelectedAuction(record);
                            setShareModalVisible(true);
                        }
                    },
                    {
                        key: 'qr',
                        label: 'Show QR Code',
                        icon: <QrcodeOutlined />,
                        onClick: () => {
                            setSelectedAuction(record);
                            setQrModalVisible(true);
                        }
                    },
                    record.is_private && {
                        type: 'divider'
                    },
                    // Section 4: Private Auction Features
                    record.is_private && {
                        key: 'invite',
                        label: 'Invite Participants',
                        icon: <UserAddOutlined />,
                        onClick: () => {
                            setSelectedAuction(record);
                            setInviteModalVisible(true);
                        },
                        disabled: record.status === 'completed' || record.status === 'cancelled'
                    },
                    record.is_private && {
                        key: 'direct_link',
                        label: 'Generate Direct Link',
                        icon: <LinkOutlined />,
                        onClick: () => {
                            setSelectedAuction(record);
                            setDirectLinkModalVisible(true);
                        },
                        disabled: record.status === 'completed' || record.status === 'cancelled'
                    },
                    record.is_private &&
                    record.status === 'active' &&
                    record.start_time &&
                    moment(record.start_time).isAfter(moment()) && {
                        key: 'bulk_users',
                        label: 'Upload Bulk Users',
                        icon: <TeamOutlined />,
                        onClick: () => {
                            setSelectedAuction(record);
                            setBulkUsersModalVisible(true);
                        }
                    },
                    {
                        type: 'divider'
                    },
                    // Section 5: Administrative Actions
                    {
                        key: 'duplicate',
                        label: 'Duplicate Auction',
                        icon: <CopyOutlined />,
                        onClick: () => openDuplicateModal(record)
                    },
                    {
                        type: 'divider'
                    },
                    {
                        key: 'delete',
                        label: 'Delete Auction',
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => {
                            Modal.confirm({
                                title: 'Are you sure you want to delete this auction?',
                                content: 'This action cannot be undone.',
                                okText: 'Yes',
                                okType: 'danger',
                                cancelText: 'No',
                                onOk: () => handleDelete(record)
                            });
                        },
                        disabled: record.status === 'completed' || record.status === 'cancelled'
                    }
                ].filter(Boolean); // Remove any null/undefined items

                return (
                    <Dropdown
                        menu={{ items }}
                        trigger={['click']}
                    >
                        <Button icon={<MoreOutlined />} />
                    </Dropdown>
                );
            }
        }
    ];

    // Column visibility control
    const { visibleColumns, ColumnVisibilityUI } = useColumnVisibility({
        baseColumns: columns,
        storageKey: 'userAuctionsColumnsVisibility'
    });
    const filteredColumns = visibleColumns;

    const handleCreateAuctionClick = () => {
        setCreateAuctionInstructionsVisible(true);
    };

    const handleProceedToCreateAuction = () => {
        setCreateAuctionInstructionsVisible(false);
        navigate('/auctions/create');
    };

    return (
        <DashboardLayout>
            <PageHeader
                title="My Auctions"
                description="Manage and track your auction listings"
                buttonText="Create Auction"
                buttonIcon={<PlusOutlined />}
                onButtonClick={handleCreateAuctionClick}
            />

            <div
                onClick={() => navigate('/auctions/easy-create')}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    background: 'linear-gradient(135deg, #f6ffed 0%, #e6f4ff 100%)',
                    border: '2px solid #52c41a',
                    borderLeft: '6px solid #52c41a',
                    borderRadius: 12,
                    padding: '16px 20px',
                    margin: '0 24px 24px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(82, 196, 26, 0.25)',
                    transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(82, 196, 26, 0.35)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(82, 196, 26, 0.25)';
                    e.currentTarget.style.transform = 'translateY(0)';
                }}
            >
                <Space size="middle">
                    <SmileOutlined style={{ fontSize: 26, color: '#52c41a' }} />
                    <div>
                        <Space size={8} align="center">
                            <Text strong style={{ fontSize: 16 }}>New here? Try Quick Mode</Text>
                            <Tag color="green" style={{ fontWeight: 700, fontSize: 11, borderRadius: 10, margin: 0 }}>
                                NEW
                            </Tag>
                        </Space>
                        <Text type="secondary" style={{ display: 'block', fontSize: 13, marginTop: 2 }}>
                            A simple, step-by-step way to create an auction and add items
                        </Text>
                    </div>
                </Space>
                <Button
                    type="primary"
                    style={{
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        border: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(82, 196, 26, 0.4)'
                    }}
                >
                    Try It <ArrowRightOutlined />
                </Button>
            </div>

            {!currentUser?.has_auctions ? (
                <NoAuctionsFound 
                    title="No Auctions Yet"
                    description="Start your auction journey by creating your first auction. You can create public or private auctions and invite participants."
                    buttonText="Create Your First Auction"
                />
            ) : (
                <div>
                    {/* Column Visibility Control */}
                    {ColumnVisibilityUI}

                    {/* Quick Actions Toolbar */}
                    <QuickActionsToolbar
                        showQuickFilters={false}
                        onExportCSV={handleExportToCSV}
                        exportDisabled={filteredAuctions.length === 0}
                    />

                    <div style={{ overflowX: 'auto' }}>
                        <Table
                            columns={filteredColumns}
                            dataSource={auctions}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} auction${total !== 1 ? 's' : ''}`,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                defaultPageSize: 10
                            }}
                            onChange={handleTableChange}
                            scroll={{ x: 1200 }}
                            size="middle"
                            className="universal-table"
                            locale={{
                                emptyText: loading
                                    ? null // Don't show empty text when loading
                                    : 'No auctions found'
                            }}
                        />
                    </div>
                </div>
            )}

            {selectedAuction && (
                <>
                    {inviteModalVisible && (
                        <InviteParticipants
                            auction={selectedAuction}
                            visible={inviteModalVisible}
                            onClose={() => {
                                setInviteModalVisible(false);
                                setSelectedAuction(null);
                            }}
                            onSuccess={() => {
                                loadAuctions();
                            }}
                        />
                    )}
                    {bulkUsersModalVisible && (
                        <BulkUploadUsers
                            auction={selectedAuction}
                            visible={bulkUsersModalVisible}
                            onClose={() => {
                                setBulkUsersModalVisible(false);
                                setSelectedAuction(null);
                            }}
                            onSuccess={() => {
                                loadAuctions();
                            }}
                        />
                    )}
                    {directLinkModalVisible && (
                        <DirectLinkManager
                            auction={selectedAuction}
                            visible={directLinkModalVisible}
                            onClose={() => {
                                setDirectLinkModalVisible(false);
                                setSelectedAuction(null);
                            }}
                        />
                    )}
                    <ShareAuctionModal
                        auction={selectedAuction}
                        visible={shareModalVisible}
                        onClose={() => {
                            setShareModalVisible(false);
                            setSelectedAuction(null);
                        }}
                    />
                    <AuctionQRCodeModal
                        visible={qrModalVisible}
                        onClose={() => {
                            setQrModalVisible(false);
                            setSelectedAuction(null);
                        }}
                        auction={selectedAuction}
                    />
                </>
            )}

            {/* Duplicate Title Modal */}
            <Modal
                title="Duplicate Auction"
                open={duplicateTitleModalVisible}
                onCancel={() => {
                    if (duplicateSubmitting) return;
                    setDuplicateTitleModalVisible(false);
                    setAuctionToDuplicate(null);
                    setDuplicateTitle('');
                    setSelectedLotIdsToDuplicate([]);
                    setLotTitleOverrides({});
                    setDuplicateSourceLots([]);
                }}
                onOk={handleDuplicate}
                okText="Duplicate"
                confirmLoading={duplicateSubmitting}
                width={560}
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                        Enter a title for the new auction. A unique URL slug will be generated automatically.
                    </Text>
                </div>
                <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Auction Title <Text type="danger">*</Text>
                    </Text>
                    <Input
                        value={duplicateTitle}
                        onChange={(e) => setDuplicateTitle(e.target.value)}
                        placeholder="Enter auction title"
                        maxLength={255}
                        onPressEnter={handleDuplicate}
                        disabled={duplicateSubmitting}
                    />
                </div>

                <div>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                        Copy some lots too? <Text type="secondary" style={{ fontWeight: 400 }}>(optional, up to {MAX_DUPLICATE_LOTS})</Text>
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 12 }}>
                        Selected lots are copied without their images. You can rename them below.
                    </Text>

                    {loadingDuplicateSourceLots ? (
                        <div style={{ textAlign: 'center', padding: '16px 0' }}>
                            <Spin size="small" />
                        </div>
                    ) : duplicateSourceLots.length === 0 ? (
                        <Empty
                            description="This auction has no lots to copy"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ margin: '16px 0' }}
                        />
                    ) : (
                        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                {duplicateSourceLots.map((lot) => {
                                    const isSelected = selectedLotIdsToDuplicate.includes(lot.id);
                                    const isMaxReached = !isSelected && selectedLotIdsToDuplicate.length >= MAX_DUPLICATE_LOTS;
                                    return (
                                        <div key={lot.id}>
                                            <Checkbox
                                                checked={isSelected}
                                                disabled={isMaxReached || duplicateSubmitting}
                                                onChange={() => toggleLotSelectionForDuplicate(lot.id)}
                                            >
                                                <Space size="small">
                                                    <span>{lot.title}</span>
                                                    <Tag>PKR {parseFloat(lot.starting_price || 0).toFixed(2)}</Tag>
                                                </Space>
                                            </Checkbox>
                                            {isSelected && (
                                                <Input
                                                    size="small"
                                                    placeholder="Lot title in the new auction"
                                                    value={lotTitleOverrides[lot.id] ?? lot.title}
                                                    onChange={(e) => handleLotTitleOverrideChange(lot.id, e.target.value)}
                                                    disabled={duplicateSubmitting}
                                                    style={{ marginTop: 6, marginLeft: 24, width: 'calc(100% - 24px)' }}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </Space>
                        </div>
                    )}
                    {selectedLotIdsToDuplicate.length > 0 && (
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 8 }}>
                            {selectedLotIdsToDuplicate.length}/{MAX_DUPLICATE_LOTS} lots selected
                        </Text>
                    )}
                </div>
            </Modal>

            {/* Duplicate Success Modal - Independent of selectedAuction */}
            {duplicatedAuction && (
                <Modal
                    title={null}
                    open={duplicateModalVisible}
                    onCancel={() => {
                        setDuplicateModalVisible(false);
                        setDuplicatedAuction(null);
                    }}
                    footer={null}
                    centered
                    width={1000}
                    closable={true}
                    maskClosable={true}
                    className="duplicate-success-modal"
                    style={{ padding: 0 }}
                >
                    <div style={{ 
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                        borderRadius: '12px',
                        overflow: 'hidden'
                    }}>
                        {/* Header Section */}
                        <div style={{
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            padding: '20px 24px 16px 24px',
                            textAlign: 'center',
                            borderBottom: 'none'
                        }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                margin: '0 auto 12px',
                                background: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backdropFilter: 'blur(10px)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                            }}>
                                <CheckCircleOutlined style={{ fontSize: '24px', color: 'white' }} />
                            </div>
                            <Title level={4} style={{ 
                                color: 'white', 
                                margin: 0,
                                fontWeight: 600,
                                fontSize: '20px'
                            }}>
                                Auction Duplicated Successfully
                            </Title>
                            <Text style={{ 
                                color: 'rgba(255, 255, 255, 0.9)', 
                                fontSize: '13px',
                                display: 'block',
                                marginTop: '6px'
                            }}>
                                Your new auction is ready to customize
                            </Text>
                        </div>

                        {/* Content Section */}
                        <div style={{ padding: '24px' }}>
                            {/* New Auction Title - Full Width */}
                            <div style={{ 
                                marginBottom: '20px',
                                padding: '16px',
                                background: '#f0f8ff',
                                borderRadius: '8px',
                                border: '1px solid #d6e4ff'
                            }}>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                    New Auction Created:
                                </Text>
                                <Text strong style={{ fontSize: '16px', color: '#1890ff', fontWeight: 600 }}>
                                    {duplicatedAuction?.title || 'N/A'}
                                </Text>
                            </div>

                            {/* Two Column Layout */}
                            <Row gutter={[20, 20]}>
                                {/* Left Column - Important Information */}
                                <Col xs={24} md={12}>
                                    <Alert
                                        message={<Text strong style={{ fontSize: '14px' }}>Important Information</Text>}
                                        description={
                                            <div style={{ marginTop: 8 }}>
                                                <Text style={{ fontSize: '13px', display: 'block', marginBottom: 12 }}>
                                                    Your auction has been duplicated successfully! The new auction has been created with the following changes:
                                                </Text>
                                                <ul style={{ margin: 0, paddingLeft: 20, fontSize: '13px', lineHeight: '1.8' }}>
                                                    <li style={{ marginBottom: 6 }}>
                                                        <strong>Start Time:</strong> Set to one month ahead ({duplicatedAuction?.start_time ? moment(duplicatedAuction.start_time).format('MMMM Do, YYYY [at] h:mm A') : 'N/A'})
                                                    </li>
                                                    <li style={{ marginBottom: 6 }}>
                                                        <strong>Status:</strong> Set to "Scheduled" - payment required to activate
                                                    </li>
                                                    <li style={{ marginBottom: 6 }}>
                                                        <strong>Title:</strong> Set to your chosen auction name
                                                    </li>
                                                    <li style={{ marginBottom: 6 }}>
                                                        <strong>Agents:</strong> Copied from the original auction (if any)
                                                    </li>
                                                    <li style={{ marginBottom: 6 }}>
                                                        <strong>Images:</strong> NOT copied - upload images manually
                                                    </li>
                                                    <li>
                                                        <strong>Lots:</strong> {duplicatedLotsCount > 0
                                                            ? `${duplicatedLotsCount} lot${duplicatedLotsCount > 1 ? 's' : ''} copied (without images)`
                                                            : 'NOT copied - create lots manually'}
                                                    </li>
                                                </ul>
                                            </div>
                                        }
                                        type="info"
                                        showIcon
                                        icon={<InfoCircleOutlined />}
                                        style={{ 
                                            height: '100%',
                                            borderRadius: '8px',
                                            border: '1px solid #91d5ff'
                                        }}
                                    />
                                </Col>

                                {/* Right Column - Next Steps */}
                                <Col xs={24} md={12}>
                                    <div style={{ 
                                        background: '#e6f7ff', 
                                        padding: '16px', 
                                        borderRadius: '8px', 
                                        border: '1px solid #91d5ff',
                                        height: '100%'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                            <Text strong style={{ color: '#1890ff', fontSize: '14px' }}>
                                                📋 Next Steps:
                                            </Text>
                                        </div>
                                        <ol style={{ margin: 0, paddingLeft: 20, fontSize: '13px', lineHeight: '1.8' }}>
                                            <li style={{ marginBottom: 6 }}>Review and update auction details (title, description, start time)</li>
                                            <li style={{ marginBottom: 6 }}>Upload images for the auction</li>
                                            <li style={{ marginBottom: 6 }}>Create lots for the auction</li>
                                            <li style={{ marginBottom: 6 }}>Complete payment to activate the auction</li>
                                            <li>If private: invite participants, generate direct links, or upload bulk users via CSV (after approval, before start)</li>
                                        </ol>
                                    </div>
                                </Col>
                            </Row>

                            {/* Footer Actions */}
                            <div style={{ 
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px',
                                paddingTop: '20px',
                                marginTop: '20px',
                                borderTop: '1px solid #f0f0f0'
                            }}>
                                <Button
                                    onClick={() => {
                                        setDuplicateModalVisible(false);
                                        setDuplicatedAuction(null);
                                    }}
                                    size="large"
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        padding: '0 24px',
                                        height: '40px'
                                    }}
                                >
                                    Close
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        setDuplicateModalVisible(false);
                                        navigate(`/auctions/${duplicatedAuction?.slug}/edit`);
                                    }}
                                    size="large"
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        padding: '0 24px',
                                        height: '40px',
                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                        border: 'none'
                                    }}
                                >
                                    Edit Auction Now
                                </Button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Create Auction Instructions Modal */}
            <Modal
                title={null}
                open={createAuctionInstructionsVisible}
                onCancel={() => setCreateAuctionInstructionsVisible(false)}
                footer={null}
                centered
                width={580}
                closable={true}
                maskClosable={true}
                className="create-auction-instructions-modal"
                style={{ padding: 0 }}
            >
                <div style={{ 
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                }}>
                    {/* Header Section */}
                    <div style={{
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        padding: '20px 24px 16px 24px',
                        textAlign: 'center',
                        borderBottom: 'none'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            margin: '0 auto 12px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}>
                            <PlusOutlined style={{ fontSize: '24px', color: 'white' }} />
                        </div>
                        <Title level={4} style={{ 
                            color: 'white', 
                            margin: 0,
                            fontWeight: 600,
                            fontSize: '20px'
                        }}>
                            Create New Auction
                        </Title>
                        <Text style={{ 
                            color: 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '13px',
                            display: 'block',
                            marginTop: '6px'
                        }}>
                            Quick guide to get you started
                        </Text>
                    </div>

                    {/* Content Section */}
                    <div style={{ padding: '24px' }}>
                        <div style={{ marginBottom: '24px' }}>
                            <Text strong style={{ 
                                fontSize: '15px', 
                                color: '#262626',
                                fontWeight: 600,
                                display: 'block',
                                marginBottom: '16px'
                            }}>
                                Follow these simple steps:
                            </Text>
                            
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                {/* Step 1 */}
                                <Card
                                    hoverable={false}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #e6f7ff',
                                        background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                                    }}
                                    bodyStyle={{ padding: '16px' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                        }}>
                                            <Text strong style={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>1</Text>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <FileTextOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                                                <Text strong style={{ fontSize: '14px', color: '#262626' }}>
                                                    Fill in Auction Details
                                                </Text>
                                            </div>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                                Add title, category, start time, and basic information
                                            </Text>
                                        </div>
                                    </div>
                                </Card>

                                {/* Step 2 */}
                                <Card
                                    hoverable={false}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #e6f7ff',
                                        background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                                    }}
                                    bodyStyle={{ padding: '16px' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                        }}>
                                            <Text strong style={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>2</Text>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <PictureOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                                                <Text strong style={{ fontSize: '14px', color: '#262626' }}>
                                                    Upload Images & Description
                                                </Text>
                                            </div>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                                Add photos and write a compelling description for your auction
                                            </Text>
                                        </div>
                                    </div>
                                </Card>

                                {/* Step 3 */}
                                <Card
                                    hoverable={false}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #e6f7ff',
                                        background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                                    }}
                                    bodyStyle={{ padding: '16px' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                        }}>
                                            <Text strong style={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>3</Text>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <AppstoreOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                                                <Text strong style={{ fontSize: '14px', color: '#262626' }}>
                                                    Create Lots
                                                </Text>
                                            </div>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                                Add individual lots with details, images, and pricing
                                            </Text>
                                        </div>
                                    </div>
                                </Card>

                                {/* Step 4 */}
                                <Card
                                    hoverable={false}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #e6f7ff',
                                        background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                                    }}
                                    bodyStyle={{ padding: '16px' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                        }}>
                                            <Text strong style={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>4</Text>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <CreditCardOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                                                <Text strong style={{ fontSize: '14px', color: '#262626' }}>
                                                    Complete Payment
                                                </Text>
                                            </div>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                                Activate your scheduled auction by completing the payment
                                            </Text>
                                        </div>
                                    </div>
                                </Card>
                            </Space>
                        </div>

                

                        {/* Footer Actions */}
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            paddingTop: '16px',
                            borderTop: '1px solid #f0f0f0'
                        }}>
                            <Button
                                onClick={() => setCreateAuctionInstructionsVisible(false)}
                                size="large"
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    padding: '0 24px',
                                    height: '40px'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleProceedToCreateAuction}
                                size="large"
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    padding: '0 24px',
                                    height: '40px',
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    border: 'none'
                                }}
                            >
                                Create Auction
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal Styles */}
            <style>{`
                .share-auction-modal .ant-modal-content,
                .duplicate-success-modal .ant-modal-content,
                .create-auction-instructions-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }
                
                .share-auction-modal .ant-modal-close,
                .duplicate-success-modal .ant-modal-close,
                .create-auction-instructions-modal .ant-modal-close {
                    display: flex;
                    font-size: 16px;
                    font-style: normal;
                    line-height: 32px;
                    justify-content: center;
                    text-transform: none;
                    text-rendering: auto;
                    color: #ffffff;
                    padding: 10px;
                    background: #333333;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    top: 12px;
                    right: 12px;
                }
                
                .share-auction-modal .ant-modal-close:hover,
                .duplicate-success-modal .ant-modal-close:hover,
                .create-auction-instructions-modal .ant-modal-close:hover {
                    color: white;
                }
            `}</style>
        </DashboardLayout>
    );
};

export default UserAuctions; 